import { z } from 'zod';
import { customerTypeSchema } from './customer.schema';
import { listQuerySchema } from '../common/pagination';

/** Auswahl fürs Geschlecht — dieselben Werte wie im Altdaten-Export. */
export const CONTACT_GENDERS = ['männlich', 'weiblich', 'divers'] as const;
/** Sprachen der Korrespondenz (Altdaten: DE/EN). */
export const CONTACT_LANGUAGES = [
  { value: 'DE', label: 'Deutsch' },
  { value: 'EN', label: 'Englisch' },
] as const;

/**
 * Altdaten am Ansprechpartner (2026-10-06): Spalten aus dem Kontakt-Import,
 * für die es kein eigenes Feld gibt. Gespeichert unter diesen Schlüsseln in
 * `legacyData`, angezeigt nur lesend im Block „Altdaten". Die Reihenfolge hier
 * ist die Anzeige-Reihenfolge.
 */
export const CONTACT_LEGACY_FIELDS = [
  { key: 'gesellschaft', label: 'Gesellschaft' },
  { key: 'mandant_quelle', label: 'Mandant (Quelle)' },
  { key: 'firma_nummer_alt', label: 'Firmennummer (alt)' },
  { key: 'lfd_nr', label: 'Laufende Nummer' },
  { key: 'name', label: 'Name (komplett)' },
  { key: 'quelldatei', label: 'Quelldatei' },
] as const;
export type ContactLegacyKey = (typeof CONTACT_LEGACY_FIELDS)[number]['key'];

export const createContactSchema = z.object({
  firstName: z.string().trim().min(1).max(120),
  lastName: z.string().trim().min(1).max(120),
  /** Briefanrede, z. B. „Sehr geehrter Herr Hubweber,". */
  salutation: z.string().trim().max(200).optional(),
  /** Akademischer Titel, z. B. „Dipl.-Ing.". */
  title: z.string().trim().max(60).optional(),
  gender: z.string().trim().max(20).optional(),
  language: z.string().trim().max(10).optional(),
  // '' = Feld geleert (Bearbeiten) — der Server speichert dann null.
  email: z.union([z.string().trim().email().max(200), z.literal('')]).optional(),
  phone: z.string().trim().max(50).optional(),
  mobile: z.string().trim().max(50).optional(),
  position: z.string().trim().max(120).optional(),
  department: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(2000).optional(),
  isPrimary: z.boolean().default(false),
});
export type CreateContactInput = z.infer<typeof createContactSchema>;

export const updateContactSchema = createContactSchema.partial().extend({
  /** Neue Adresse (Kunde/Lieferant) — verschiebt den Ansprechpartner. */
  customerId: z.string().uuid().optional(),
});
export type UpdateContactInput = z.infer<typeof updateContactSchema>;

export const contactResponseSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  mobile: z.string().nullable(),
  position: z.string().nullable(),
  department: z.string().nullable(),
  notes: z.string().nullable(),
  isPrimary: z.boolean(),
  /** Zusatzfelder aus Altdaten-Import (Ansprechpartner). */
  salutation: z.string().nullable(),
  title: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.string().nullable(),
  language: z.string().nullable(),
  fax: z.string().nullable(),
  phoneInternal: z.string().nullable(),
  website: z.string().nullable(),
  /** Übrige Altdaten-Spalten (Schlüssel aus `CONTACT_LEGACY_FIELDS`); null = keine. */
  legacyData: z.record(z.string(), z.string()).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ContactResponse = z.infer<typeof contactResponseSchema>;

/** Eintrag im globalen Ansprechpartner-Verzeichnis (mit Adress-Bezug). */
export const contactDirectoryItemSchema = contactResponseSchema.extend({
  customerName: z.string(),
  customerType: customerTypeSchema,
});
export type ContactDirectoryItem = z.infer<typeof contactDirectoryItemSchema>;

/** Listen-Query fürs Ansprechpartner-Verzeichnis: Pagination + Filter. */
export const contactDirectoryQuerySchema = listQuerySchema.extend({
  customerType: customerTypeSchema.optional(),
  isPrimary: z.coerce.boolean().optional(),
});
export type ContactDirectoryQuery = z.infer<typeof contactDirectoryQuerySchema>;

/**
 * Anzeigename eines Ansprechpartners für Belege („z. Hd. Dr. Eva Muster"):
 * Titel, Vor- und Nachname, Leerteile ausgelassen. Die Anrede („Herr"/„Frau")
 * bleibt bewusst draußen — hinter „z. Hd." wäre sie grammatisch falsch
 * („Herr" statt „Herrn") und im Englischen ohnehin unüblich.
 */
export function formatContactDisplayName(contact: {
  title?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): string {
  return [contact.title, contact.firstName, contact.lastName]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ');
}
